import {
    init,
    reactRouterV5BrowserTracingIntegration,
} from '@sentry/react';
import { createBrowserHistory } from 'history';
import 'react-app-polyfill/ie11';
import 'react-app-polyfill/stable';
import ReactDOM from 'react-dom';
import { IntlProvider } from 'react-intl';
import App from './App';
import StorageErrorBoundary from './components/common/StorageErrorBoundary';
import APP_VERSION from './config/version';
import './index.css';
import * as serviceWorker from './serviceWorker';
import configureStore from './store/store';
import german from './translations/de.json';
import english from './translations/en.json';
import french from './translations/fr.json';
import italian from './translations/it.json';

// Create history for React Router v5 tracing
const history = createBrowserHistory();

// Sentry configuration - https://docs.sentry.io/platforms/javascript/guides/react/
init({
    dsn: 'https://af62b20837224d26a160c86ce0493402@sentry.io/1861049',
    // Deduce app name and environment from default URL
    // prod: swisskischool.ch | preprod: preprod.swisskischool.ch | dev: dev.swisskischool.ch
    release: (() => {
        const url = process.env.REACT_APP_FRONTEND_DEFAULT_URL || '';
        const parts = url.split('.');
        // If URL starts with preprod/dev prefix, take second part, otherwise first
        const appName = (parts[0] === 'preprod' || parts[0] === 'dev') ? parts[1] : parts[0];
        // Get version from package.json, fallback to REACT_APP_VERSION or 'dev'
        const version = APP_VERSION || process.env.REACT_APP_VERSION || 'dev';
        return `${appName || 'snowpros'}@${version}`;
    })(),
    environment: (() => {
        const url = process.env.REACT_APP_FRONTEND_DEFAULT_URL || '';
        if (url.startsWith('preprod.')) return 'preprod';
        if (url.startsWith('dev.')) return 'development';
        return 'production';
    })(),

    // React Router v5 integration for performance tracing
    integrations: [
        reactRouterV5BrowserTracingIntegration({ history }),
    ],

    // Performance monitoring - sample 5% of transactions to save quota
    tracesSampleRate: 0.05,

    // Declarative error filtering - cleaner than beforeSend for simple patterns
    // https://docs.sentry.io/platforms/javascript/configuration/filtering/
    ignoreErrors: [
        // Browser extensions noise
        'Object Not Found Matching Id', // LastPass (SWISSKISCHOOL-FRONTEND-29E)
        /^Non-Error promise rejection captured/, // Extensions rejecting with non-Error objects
        'instantSearchSDKJSBridgeClearHighlight', // Safari extensions
        'instantSearchSDKJSBridgeClear', // Safari extensions
        // Browser/extension injected runtime noise (SWISSKISCHOOL-FRONTEND-2E8)
        /^Invalid call to runtime\.sendMessage\(\)\. Tab not found\.$/,

        // Browser-specific noise
        'ResizeObserver loop limit exceeded',
        'ResizeObserver loop completed with undelivered notifications',

        // Sentry SDK internal race condition (SWISSKISCHOOL-FRONTEND-2GS)
        // Occurs when user interacts before Sentry is fully initialized
        'feature toggles accessed before being set',

        // Network errors (usually transient)
        'Network request failed',
        'Failed to fetch',
        'Load failed',
        'NetworkError',
        'AbortError',
        'ChunkLoadError',
    ],

    // Block errors from browser extension URLs
    denyUrls: [
        /extensions\//i,
        /^chrome(-extension)?:\/\//i,
        /^moz-extension:\/\//i,
        /^safari(-web)?-extension:\/\//i,
        /^ms-browser-extension:\/\//i,
    ],

    // Only send errors from your domains (optional, stricter filtering)
    allowUrls: [
        /^https?:\/\/(.*\.)?swisskischool\.ch/,
        /^https?:\/\/(.*\.)?skischoolalliance\.com/,
        /^https?:\/\/(.*\.)?booking-sport\.ch/,
        /^https?:\/\/(.*\.)?booking-formation\.ch/,
        /^https?:\/\/localhost/,
    ],

    // Keep beforeSend only for complex filtering that can't be done declaratively
    beforeSend(event) {
        // Filter cross-origin errors on help page
        if (event.request?.url?.includes('help')
            && event.exception?.values?.[0]?.value?.includes('cross-origin')) {
            return null;
        }
        // Filter localhost dev errors
        if (event.request?.url?.includes(':3000')) {
            return null;
        }
        return event;
    },
});

const store = configureStore();

const messages = {
    en: english,
    fr: french,
    de: german,
    it: italian,
};

const getInitialLang = (): keyof typeof messages => {
    if (typeof window === 'undefined') return 'fr';
    const maybeLang = window.location.pathname.split('/')[1] as keyof typeof messages;
    if (maybeLang && messages[maybeLang]) return maybeLang;
    return 'fr';
};

const initialLang = getInitialLang();

ReactDOM.render(
    <IntlProvider locale={initialLang} messages={messages[initialLang]}>
        <StorageErrorBoundary>
            <App store={store} />
        </StorageErrorBoundary>
    </IntlProvider>,
    document.getElementById('root'),
);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();

// Retrieve Firebase Messaging object.
// const firebaseConfig = {
//     apiKey: "AIzaSyDHvf7xDHy5P5MX2Rb5L2I29lfLL12IlyM",
//     authDomain: "badisay-844b6.firebaseapp.com",
//     databaseURL: "https://badisay-844b6.firebaseio.com",
//     projectId: "badisay-844b6",
//     storageBucket: "badisay-844b6.appspot.com",
//     messagingSenderId: "182039356902",
//     appId: "1:182039356902:web:6bc305237949da1e"
// };
